import { Button, ErrorScreen, UnpluggedArt } from '@/components';
import { Navigate, useLocation } from '@solidjs/router';
import { ApiClientError } from '@uchat-team-management/shared';
import { type JSX, Show, createSignal } from 'solid-js';
import { useAuth } from './AuthProvider';

// Only an explicit identity rejection means "not authorized"; any other /me
// failure (5xx, network) is a server problem and must not claim lack of access.
const isIdentityRejection = (err: unknown): boolean =>
  err instanceof ApiClientError &&
  (err.code === 'EMAIL_NOT_AUTHORIZED' || err.code === 'ACCOUNT_DISABLED');

const rejectionHref = (err: unknown): string =>
  err instanceof ApiClientError && err.code === 'ACCOUNT_DISABLED'
    ? '/authorize-error?reason=disabled'
    : '/authorize-error';

// Non-rejection /me failure: an unplugged cable that snaps apart; plugs rejoin while retrying.
function LoadError(props: { onRetry: () => Promise<void> }): JSX.Element {
  const [retrying, setRetrying] = createSignal(false);
  const retry = async () => {
    setRetrying(true);
    try {
      await props.onRetry();
    } finally {
      setRetrying(false);
    }
  };
  return (
    <ErrorScreen
      art={<UnpluggedArt retrying={retrying()} />}
      title="Failed to load your account"
      body="Something went wrong while loading your account. Please try again."
      action={
        <Button
          size="lg"
          class="min-w-44 shadow-lg shadow-brand-600/30"
          loading={retrying()}
          onClick={() => void retry()}
        >
          {retrying() ? 'Retrying…' : 'Try again'}
        </Button>
      }
    />
  );
}

export default function RequireAuth(props: { children: JSX.Element }): JSX.Element {
  const { session, firebaseUser, sessionError, refresh } = useAuth();
  const location = useLocation();
  return (
    <Show
      when={session() !== undefined}
      fallback={
        <div class="flex min-h-screen items-center justify-center">
          <div class="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        </div>
      }
    >
      <Show
        when={session() !== null}
        fallback={
          <Show when={firebaseUser()} fallback={<Navigate href="/login" />}>
            <Show
              when={isIdentityRejection(sessionError())}
              fallback={<LoadError onRetry={refresh} />}
            >
              <Navigate href={rejectionHref(sessionError())} />
            </Show>
          </Show>
        }
      >
        {/* Forced-profile: no navigation until a display name is saved. */}
        <Show
          when={!session()?.mustSetName || location.pathname === '/profile'}
          fallback={<Navigate href="/profile" />}
        >
          {props.children}
        </Show>
      </Show>
    </Show>
  );
}
